import {
  Component,
  OnInit,
  OnChanges,
  Input,
  EventEmitter,
  Output
} from "@angular/core";
import { MonitoredTrain } from "../../models/monitored-train";
import { ReferenceArea } from "../../../reference-areas/models/reference-area";
import { environment } from "../../../../../environments/environment";
import { ReferenceAreaDataService } from '../../../reference-areas/services/reference-area-data.service';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { UserProfileService } from '../../../users/services/user-profile.service';

declare const google; 

@Component({
  selector: "app-monitored-trains-map",
  template: require('./monitored-trains-map.component.html'),
  styles: [require('./monitored-trains-map.component.sass')]
})
export class MonitoredTrainsMapComponent implements OnInit, OnChanges {
  @Input() projectId: number;
  @Input() trains: MonitoredTrain[];
  @Input() zoom: number;
  @Input() showTrainState: boolean;
  @Input() markOfflineTrain: boolean;
  @Input() isSingleTrain: boolean = false;
  @Input() referenceAreas: ReferenceArea[];
  @Input() mapClass: string;
  @Input() mapLegendPosition: string;

  @Output() investigateTrain = new EventEmitter<MonitoredTrain>();
  @Output() reload = new EventEmitter();

  usedReferenceIds: (number | undefined)[];
  searchTrain: string;
  legends: any[];
  drawingOptions = {
    drawingMode: null,
    drawingControl: false,
    drawingControlOptions: {
      position: google.maps.ControlPosition.TOP_CENTER,
      drawingModes: [google.maps.drawing.OverlayType.POLYGON]
    },
    polygonOptions: {
      strokeOpactity: 0.8,
      strokeColor: "#FF5757",
      fillColor: "#FF5757",
      fillOpacity: 0.25,
      strokeWeight: 2,
      clickable: true,
      editable: false,
      zIndex: 1
    }
  };
  drawingManager = new google.maps.drawing.DrawingManager(this.drawingOptions);
  map: any;

  showPolygons = true;
  polygonInfoWindow = new google.maps.InfoWindow();

  selectedReferenceArea: any;
  activePolygon: any;

  defaultLocation: {
    lat: number,
    lng: number
  }

  trainsMap: any;

  constructor(
    private referenceAreaDataService: ReferenceAreaDataService,
    private translate: TranslateService,
    private confirmationDialogService: ConfirmationDialogService,
    private messageService: MessageService,
    private userProfileService: UserProfileService
  ) {}

  ngOnInit(): void {
    this.drawingManager.setMap(null);
  }

  ngOnChanges(): void {
    if (this.trains && this.trains.length > 1) {
      this.drawingManager.setOptions({
          drawingControl: this.userProfileService.isAdmin()
       });
    } 
    this.legends = [];
    if (this.referenceAreas) {
      const referenceAreaTypes = this.referenceAreas.reduce(
        (acc, reference) => {
          acc[reference.type.toLowerCase()] = true;
          return acc;
        },
        {}
      );
      this.legends = [
        ...this.legends,
        ...Object.keys(referenceAreaTypes)
          .map(type => {
            const typeLegend = environment.config.map.legends.find(legend =>
              legend.name.includes(type.toLowerCase())
            );
            return typeLegend;
          })
          .filter(legend => !!legend)
      ];
    }

    let mapLegend = localStorage.getItem('map-legend');
    if (mapLegend) {
      const parsedMapLegend: any[] = JSON.parse(mapLegend);
      this.legends = this.legends.map(legend => {
        const currentLegend = parsedMapLegend && parsedMapLegend.find(localLegend => localLegend.name === legend.name);
        return currentLegend ? {
          ...legend,
          ...currentLegend
        } : legend
      })
    } else {
      localStorage.setItem('map-legend', JSON.stringify(this.legends));
    }

    

    this.referenceAreas = this.referenceAreas
      ? this.referenceAreas
          .map(referenceArea => this.processReferenceArea(referenceArea))
          .sort((p1, p2) => (p1.surface > p2.surface ? -1 : 1))
      : [];

    this.usedReferenceIds = this.referenceAreas ? this.referenceAreas.map(referenceArea => referenceArea.id) : [];

    if (this.trains && this.trains.length === 1) {
      this.defaultLocation = {
        lat: this.trains[0].currentPosition.latitude,
        lng: this.trains[0].currentPosition.longitude
      }
    }

    this.trainsMap = JSON.parse(localStorage.getItem('trainsMap') || "{}");
  }

  processReferenceArea = (referenceArea: ReferenceArea) => {
    const paths = referenceArea.area && referenceArea.area.positions ? referenceArea.area.positions.map(pos => {
      return new google.maps.LatLng(pos.latitude, pos.longitude);
    }) : [];
    const bounds = new google.maps.LatLngBounds();
    paths.forEach(path => {
      bounds.extend(path);
    });
    return {
      ...referenceArea,
      surface: google.maps.geometry.spherical.computeArea(paths),
      polygonCenter: bounds.getCenter(),
      paths
    };
  }

  zoomChange = (zoom: number) => {
    this.zoom = zoom;
  }

  onPolyClick = async (referenceArea: any) => {
    this.selectedReferenceArea = { ...referenceArea };
  };

  onSelectedReferenceAreaClose = () => {
    this.selectedReferenceArea = null;
  };

  onInvestigateTrain = (train: MonitoredTrain) => {
    this.investigateTrain.emit(train);
  };

  onMouseClick(infoWindow, train: MonitoredTrain) {
    this.trainsMap = JSON.parse(localStorage.getItem('trainsMap') || "{}");
    const highestZIndex = Object.keys(this.trainsMap).reduce((acc, trainKey) => Math.max(acc, this.trainsMap[trainKey]?.zIndex) , 0);
    this.trainsMap[train.uid] = {
      zIndex: highestZIndex + 1
    }
    localStorage.setItem('trainsMap', JSON.stringify(this.trainsMap));
    infoWindow.open();
  }

  trainInfoClose = (trainUid: string) => {
    this.trainsMap = JSON.parse(localStorage.getItem('trainsMap') || "{}");
    this.trainsMap[trainUid] = undefined;
    localStorage.setItem('trainsMap', JSON.stringify(this.trainsMap));
  }

  onMapReady(map) {
    this.map = map;
    this.drawingManager.setMap(this.map);
    google.maps.event.addListener(
      this.drawingManager,
      "polygoncomplete",
      this.onPolygonDrawingComplete
    );
  }

  onPolygonDrawingComplete = (polygon: any) => {
    this.activePolygon = polygon;
    this.selectedReferenceArea = null;
    this.drawingManager.setDrawingMode(null);
    const positions: any[] = [];
    polygon.getPath().forEach((LatLng: any) => {
      positions.push({
        latitude: LatLng.lat(),
        longitude: LatLng.lng()
      });
    });
    let newReferenceArea: any = {
      area: {
        positions
      }
    };
    const paths = newReferenceArea.area.positions.map(pos => {
      return new google.maps.LatLng(pos.latitude, pos.longitude);
    });
    const bounds = new google.maps.LatLngBounds();
    paths.forEach(path => {
      bounds.extend(path);
    });
    this.selectedReferenceArea = {
      ...newReferenceArea,
      surface: google.maps.geometry.spherical.computeArea(paths),
      polygonCenter: bounds.getCenter(),
      paths,
      projectId: this.projectId,
      latLongList: newReferenceArea.area && newReferenceArea.area.positions ? newReferenceArea.area.positions.reduce((acc, position) => {
        return acc ? acc + " " + position.latitude + "," +position.longitude :  position.latitude + "," +position.longitude
      }, '') : ''
    };

  };


  onSaveReferenceArea = async (referenceArea: ReferenceArea) => {
    try {
      await this.referenceAreaDataService.saveReferenceAreas(this.projectId, [referenceArea]);
      this.messageService.add({
        severity:'success',
        summary: this.translate.instant('I18N_REFERENCE_AREAS.SAVE_REFERENCE_AREA'),
        detail:`${this.translate.instant("I18N_REFERENCE_AREAS.THE_REFERENCE_AREA")} ${referenceArea.id ? this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_UPDATED") : this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")}`
      });
      this.reload.emit();
    } catch(error) {
      this.messageService.add({
        severity:'error',
        summary: this.translate.instant("I18N_REFERENCE_AREAS.SAVE_REFERENCE_AREA"),
        detail: error && error.message
      });
    }

    this.selectedReferenceArea = null;
    if (!referenceArea.id) {
      this.activePolygon.setMap(null);
    }
  }

  onCancelReferenceArea = () => {
    this.selectedReferenceArea = null;
    this.activePolygon.setMap(null);
  }

  onDeleteReferenceArea = async (referenceArea: ReferenceArea) => {
    if (referenceArea && referenceArea.id) {
      const confirmed = await this.confirmationDialogService.confirm(
        `${this.translate.instant('I18N_REFERENCE_AREAS.DELETE_REFERENCE_AREA')} ${referenceArea.description}`,
        this.translate.instant('I18N_REFERENCE_AREAS.DELETE_CONFIRMATION'),
        'danger',
        this.translate.instant('I18N_REFERENCE_AREAS.DELETE_REFERENCE_AREA'),
        this.translate.instant('I18N_GENERAL.CANCEL'),
        "lg");
      if (confirmed) {
        try {
          await this.referenceAreaDataService.deleteReferenceArea(referenceArea.projectId, referenceArea.id, referenceArea.type);
          this.messageService.add({
            severity:'success',
            summary: this.translate.instant('I18N_REFERENCE_AREAS.DELETE_REFERENCE_AREA'),
            detail:`${this.translate.instant("I18N_REFERENCE_AREAS.THE_REFERENCE_AREA")} ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_DELETED")}`
          });
          this.reload.emit();
        } catch (error) {
          this.messageService.add({
            severity:'error',
            summary: this.translate.instant("I18N_REFERENCE_AREAS.DELETE_REFERENCE_AREA"),
            detail: error && error.message
          });
        }
      }
    }
    this.selectedReferenceArea = null;
  }

  toggleLegendItem = item => {
    item.visible = !item.visible;
    this.legends = this.legends.map(legend => {
      if (legend.name === item.name) {
        legend = {
          ...item
        }
      }
      return legend;
    })
    localStorage.setItem('map-legend', JSON.stringify(this.legends));
  }

  uniqueTrainIdentifier = (index: number, train: MonitoredTrain): string => {
    return train.projectId + '-' + train.trainId;
  }
}